<template>
  <div class="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
    <!-- Header principal avec titre et stats -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-900">{{ $t('translation.title') }}</h2>
        <p class="text-sm text-gray-600 mt-1">
          {{ translationKeys.length }} {{ $t('translation.stats.keys') }} •
          {{ translations.length }} {{ $t('translation.stats.translations') }}
        </p>
      </div>

      <!-- Boutons d'action - Responsive avec icônes -->
      <div class="flex flex-wrap gap-2 sm:gap-3">
        <!-- Export -->
        <button
          @click="$emit('export')"
          :disabled="exportLoading"
          class="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :title="$t('translation.export.button')"
        >
          <DownloadIcon v-if="!exportLoading" class="h-4 w-4" />
          <Loader2Icon v-else class="h-4 w-4 animate-spin" />
          <span class="hidden sm:inline">{{ $t('translation.export.button') }}</span>
        </button>

        <!-- Synchroniser -->
        <button
          @click="$emit('sync')"
          :disabled="syncLoading"
          class="inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :title="$t('translation.sync.button')"
        >
          <RefreshCwIcon v-if="!syncLoading" class="h-4 w-4" />
          <Loader2Icon v-else class="h-4 w-4 animate-spin" />
          <span class="hidden sm:inline">{{ $t('translation.sync.button') }}</span>
        </button>

        <!-- Enregistrer tout (modifications en attente) -->
        <button
          v-if="pendingChangesCount > 0"
          @click="$emit('saveAll')"
          :disabled="saveAllLoading"
          class="inline-flex items-center gap-2 px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :title="$t('translation.saveAll.button')"
        >
          <SaveIcon v-if="!saveAllLoading" class="h-4 w-4" />
          <Loader2Icon v-else class="h-4 w-4 animate-spin" />
          <span class="hidden sm:inline">{{ $t('translation.saveAll.button') }}</span>
          <span
            class="inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 text-xs font-medium bg-purple-700 rounded-full"
            >{{ pendingChangesCount }}</span
          >
        </button>

        <!-- Import -->
        <button
          @click="$emit('import')"
          class="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors"
          :title="$t('translation.import.button.text')"
        >
          <UploadIcon class="h-4 w-4" />
          <span class="hidden sm:inline">{{ $t('translation.import.button.text') }}</span>
        </button>
      </div>
    </div>

    <!-- Barre de recherche -->
    <div class="relative mb-4">
      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <SearchIcon class="h-5 w-5 text-gray-400" />
      </div>
      <input
        :value="searchQuery"
        @input="$emit('update:searchQuery', ($event.target as HTMLInputElement).value)"
        type="text"
        :placeholder="$t('translation.search.placeholder')"
        class="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
      />
      <div v-if="searchQuery" class="absolute inset-y-0 right-0 pr-3 flex items-center">
        <button
          @click="$emit('update:searchQuery', '')"
          class="text-gray-400 hover:text-gray-600 transition-colors"
          :aria-label="$t('translation.filters.reset')"
        >
          <XIcon class="h-5 w-5" />
        </button>
      </div>
    </div>

    <!-- Filtres - Responsive -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
      <div class="sm:col-span-1">
        <label class="block text-sm font-medium text-gray-700 mb-1.5">{{
          $t('translation.filters.context')
        }}</label>
        <select
          :value="selectedContext"
          @change="$emit('update:selectedContext', ($event.target as HTMLSelectElement).value)"
          class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="">{{ $t('translation.filters.contextAll') }}</option>
          <option v-for="context in availableContexts" :key="context" :value="context">
            {{ context }}
          </option>
        </select>
      </div>

      <div class="sm:col-span-1">
        <label class="block text-sm font-medium text-gray-700 mb-1.5">{{
          $t('translation.filters.language')
        }}</label>
        <select
          :value="selectedLanguage"
          @change="$emit('update:selectedLanguage', ($event.target as HTMLSelectElement).value)"
          class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="">{{ $t('translation.filters.languageAll') }}</option>
          <option v-for="lang in availableLanguages" :key="lang.code" :value="lang.code">
            {{ lang.flag }} {{ lang.name }}
          </option>
        </select>
      </div>

      <div class="sm:col-span-1 flex items-end">
        <button
          @click="$emit('resetFilters')"
          class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <RotateCcwIcon class="h-4 w-4" />
          <span>{{ $t('translation.filters.reset') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  DownloadIcon,
  Loader2Icon,
  RefreshCwIcon,
  RotateCcwIcon,
  SaveIcon,
  SearchIcon,
  UploadIcon,
  XIcon,
} from 'lucide-vue-next'

interface Language {
  code: string
  name: string
  flag: string
}

interface Props {
  translationKeys: Array<{ id: number }>
  translations: Array<{ id: number }>
  exportLoading: boolean
  syncLoading: boolean
  saveAllLoading: boolean
  pendingChangesCount: number
  searchQuery: string
  selectedContext: string
  selectedLanguage: string
  availableContexts: string[]
  availableLanguages: Language[]
}

defineProps<Props>()

defineEmits<{
  export: []
  sync: []
  saveAll: []
  import: []
  'update:searchQuery': [value: string]
  'update:selectedContext': [value: string]
  'update:selectedLanguage': [value: string]
  resetFilters: []
}>()
</script>
